/**
 * The signed-in user and their ID token, for the whole app.
 *
 * Ported from `lib/auth/AuthProvider.tsx`. The shape is unchanged because the contract it
 * serves is unchanged: every API call carries `idToken` in an `Authorization` header, and a
 * freshly-granted admin claim only takes effect once the client force-refreshes its token.
 *
 * `refreshToken` is not a convenience. Custom claims live inside the ID token, so an admin who
 * has just been granted the role is still carrying a token that says otherwise until this is
 * called — which is why the admin bootstrap flow tells you to sign out and back in.
 */

import { onAuthStateChanged, type User } from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { getFirebaseAuth, signInWithGoogle, signOutUser } from "./firebase";

export interface AuthContextValue {
  user: User | null;
  /** True until the first `onAuthStateChanged` callback settles. */
  loading: boolean;
  /** Cached ID token for API calls; null when signed out. */
  idToken: string | null;
  /** Force-refreshes the token, picking up custom claims granted since sign-in. */
  refreshToken: () => Promise<string | null>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Read inside callbacks without making them depend on render state.
  const userRef = useRef<User | null>(null);

  useEffect(() => {
    let active = true;

    // The listener's own signature returns void, so the async work is a separate function
    // invoked with `void` rather than an `async` callback Firebase would never await.
    const handleUser = async (nextUser: User | null) => {
      userRef.current = nextUser;
      if (!active) return;
      setUser(nextUser);

      if (!nextUser) {
        setIdToken(null);
        setLoading(false);
        return;
      }

      try {
        const token = await nextUser.getIdToken();
        if (active) setIdToken(token);
      } catch (error) {
        // Signed in but tokenless is a real state — the app renders as signed-in and every
        // API call 401s — so it is logged rather than swallowed, and `loading` still settles.
        console.error("[auth] failed to fetch ID token:", error);
        if (active) setIdToken(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(getFirebaseAuth(), (nextUser) => {
      void handleUser(nextUser);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    const current = userRef.current;
    if (!current) {
      setIdToken(null);
      return null;
    }
    const result = await current.getIdTokenResult(true);
    setIdToken(result.token);
    return result.token;
  }, []);

  // The `onAuthStateChanged` listener above populates user and token; neither of these sets
  // state itself, so there is one path into the signed-in state rather than two.
  const signIn = useCallback(async () => {
    await signInWithGoogle();
  }, []);

  const signOut = useCallback(async () => {
    await signOutUser();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, idToken, refreshToken, signIn, signOut }),
    [user, loading, idToken, refreshToken, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider");
  return context;
}
